import libphonenumber from 'google-libphonenumber'

const { PhoneNumberUtil } = libphonenumber

export interface CountryMetadata {
  code: string // ISO country code (e.g., 'SE', 'PL')
  name: string // Country name (e.g., 'Sweden', 'Poland')
  nameKey: string // Translation key (e.g., 'sweden', 'poland')
  dialCode: string // International dial code (e.g., '+46', '+48')
  flag: string // Flag emoji (e.g., '🇸🇪', '🇵🇱')
}

// Country code to translation key mapping
const COUNTRY_NAME_KEYS: Record<string, string> = {
  // EU Member States
  AT: 'austria',
  BE: 'belgium',
  BG: 'bulgaria',
  HR: 'croatia',
  CY: 'cyprus',
  CZ: 'czechRepublic',
  DK: 'denmark',
  EE: 'estonia',
  FI: 'finland',
  FR: 'france',
  DE: 'germany',
  GR: 'greece',
  HU: 'hungary',
  IE: 'ireland',
  IT: 'italy',
  LV: 'latvia',
  LT: 'lithuania',
  LU: 'luxembourg',
  MT: 'malta',
  NL: 'netherlands',
  PL: 'poland',
  PT: 'portugal',
  RO: 'romania',
  SK: 'slovakia',
  SI: 'slovenia',
  ES: 'spain',
  SE: 'sweden',

  // EEA countries (not EU but part of European Economic Area)
  IS: 'iceland',
  LI: 'liechtenstein',
  NO: 'norway',

  // Other European countries
  CH: 'switzerland',
  GB: 'unitedKingdom',
  AL: 'albania',
  AD: 'andorra',
  BY: 'belarus',
  BA: 'bosniaAndHerzegovina',
  MK: 'northMacedonia',
  MD: 'moldova',
  MC: 'monaco',
  ME: 'montenegro',
  RS: 'serbia',
  SM: 'sanMarino',
  UA: 'ukraine',
  VA: 'vaticanCity',

  // Other commonly used countries
  US: 'unitedStates',
  CA: 'canada',
  AU: 'australia',
  NZ: 'newZealand',
  KR: 'southKorea'
}

// Fallback English names (used when translation is not available)
const COUNTRY_NAMES_FALLBACK: Record<string, string> = {
  AT: 'Austria',
  BE: 'Belgium',
  BG: 'Bulgaria',
  HR: 'Croatia',
  CY: 'Cyprus',
  CZ: 'Czech Republic',
  DK: 'Denmark',
  EE: 'Estonia',
  FI: 'Finland',
  FR: 'France',
  DE: 'Germany',
  GR: 'Greece',
  HU: 'Hungary',
  IE: 'Ireland',
  IT: 'Italy',
  LV: 'Latvia',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  MT: 'Malta',
  NL: 'Netherlands',
  PL: 'Poland',
  PT: 'Portugal',
  RO: 'Romania',
  SK: 'Slovakia',
  SI: 'Slovenia',
  ES: 'Spain',
  SE: 'Sweden',
  IS: 'Iceland',
  LI: 'Liechtenstein',
  NO: 'Norway',
  CH: 'Switzerland',
  GB: 'United Kingdom',
  AL: 'Albania',
  AD: 'Andorra',
  BY: 'Belarus',
  BA: 'Bosnia and Herzegovina',
  MK: 'North Macedonia',
  MD: 'Moldova',
  MC: 'Monaco',
  ME: 'Montenegro',
  RS: 'Serbia',
  SM: 'San Marino',
  UA: 'Ukraine',
  VA: 'Vatican City',
  US: 'United States',
  CA: 'Canada',
  AU: 'Australia',
  NZ: 'New Zealand',
  KR: 'South Korea'
}

/**
 * Gets dial code for a country using google-libphonenumber
 */
export const getDialCodeForCountry = (countryCode: string): string => {
  try {
    const phoneUtil = PhoneNumberUtil.getInstance()
    const code = phoneUtil.getCountryCodeForRegion(countryCode)
    return `+${code}`
  } catch {
    return ''
  }
}

/**
 * Gets all country metadata sorted alphabetically by name
 * @param countryFlags - Mapping of country codes to flag emojis
 * @param translateFn - Optional translation function (countryKey) => translatedName
 */
export const getAllCountries = (
  countryFlags: Record<string, string>,
  translateFn?: (key: string) => string
): CountryMetadata[] => {
  const countries = Object.keys(countryFlags).map((code) => {
    const nameKey = COUNTRY_NAME_KEYS[code] || code.toLowerCase()
    const name = translateFn ? translateFn(nameKey) : COUNTRY_NAMES_FALLBACK[code] || code

    return {
      code,
      name,
      nameKey,
      dialCode: getDialCodeForCountry(code),
      flag: countryFlags[code]
    }
  })

  // Sort alphabetically by name
  return countries.sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Finds country metadata by country code
 * @param countryCode - ISO country code
 * @param countryFlags - Mapping of country codes to flag emojis
 * @param translateFn - Optional translation function (countryKey) => translatedName
 */
export const getCountryByCode = (
  countryCode: string,
  countryFlags: Record<string, string>,
  translateFn?: (key: string) => string
): CountryMetadata | undefined => {
  const flag = countryFlags[countryCode]
  if (!flag) return undefined

  const nameKey = COUNTRY_NAME_KEYS[countryCode] || countryCode.toLowerCase()
  const name = translateFn ? translateFn(nameKey) : COUNTRY_NAMES_FALLBACK[countryCode] || countryCode

  return {
    code: countryCode,
    name,
    nameKey,
    dialCode: getDialCodeForCountry(countryCode),
    flag
  }
}
